import { Box, Stack } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import moment from "moment";
import React, { FC, memo, useEffect, useState } from "react";
import { DEFAULT_SCORING_RATING } from "../../constants";
import {
    ICohortComment,
    baseAtom
} from "../../stores/jotai/base";
import { cohortPeriodsAtom } from "../../stores/jotai/cohortPeriods";
import { scoringSelectedPeriodAtom } from "../../stores/jotai/scoring/selectedPeriodIndex";
import { useCreateComment } from "../../tanstackQuery/queries/comments";
import { buildCurrentRatings } from "../../util/scoringUtils";
import IwButton from "../app/IwButton";
import ScoringCommentPanel from "./ScoringCommentPanel";
import ScoringFeedbackHeader from "./ScoringFeedbackHeader";
import ScoringRatingPanel from "./ScoringRatingPanel";
import { toast } from "sonner";
import { renderErrorToast } from "../../../lib/ErrorToastMessage";

interface ScoringFeedbackSectionProps {
    comment?: ICohortComment;
    ratingQuestions?: string[];
}

const ScoringFeedbackSection: FC<ScoringFeedbackSectionProps> = (props) => {
    const { comment: currentComment, ratingQuestions } = props;
    const [commentText, setCommentText] = useState<string>("");
    const [currentRatings, setCurrentRatings] = useState<number[]>([]);
    const [savingComment, setSavingComment] = useState<boolean>(false);
    const [base, setBase] = useAtom(baseAtom);
    const cohortPeriods = useAtomValue(cohortPeriodsAtom);
    const selectedPeriodIndex = useAtomValue(scoringSelectedPeriodAtom);
    const { keycloak } = useKeycloak();
    const user = base?.user || null;
    const teamID = base?.team?.id;
    const cohortID = base?.cohort?.id;
    const currentPeriodID = cohortPeriods[selectedPeriodIndex]?.groupPeriodID;
    const commentRatings: number[] = buildCurrentRatings(currentComment);
    const theme = useTheme();
    const isSubmitButtonDisabled =
        (currentComment &&
            currentComment.content === commentText &&
            commentRatings.join(",") === currentRatings.join(",")) ||
        savingComment;

    const onCommentTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCommentText(event?.target?.value);
    };

    const onRatingChange = (index: number, value: number) => {
        setCurrentRatings((prevRatings) => {
            const newRatings = [...prevRatings];

            newRatings[index] = value;

            return newRatings;
        });
    };

    const client = useQueryClient();
    const { mutateAsync: createNewComment } = useCreateComment(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["comments"] });
        }
      }
    );

    const saveComment = () => {
        setSavingComment(true);
        let token = keycloak?.token;
        //@ts-ignore
        let sid = window?.__sid;
        const payload = {
            cohortID,
            slug: "instructor",
            rating: currentRatings.join(","),
            iterator: selectedPeriodIndex,
            groupID: teamID,
            content: commentText,
            commentID: currentComment?.commentID,
            period: currentPeriodID,
            sessionID: sid,
        };

        createNewComment({
          ...payload
        })
          .catch((error) => {
            renderErrorToast("Unable to create comment")
          })
          .finally(() => {
            setSavingComment(false);
          });
    };

    useEffect(() => {
        if (!!currentComment) {
            setCommentText(currentComment?.content);
            setCurrentRatings(
                ratingQuestions.map((_question, index) => commentRatings[index] || 0)
            );
        } else {
            setCommentText("");
            setCurrentRatings(
                ratingQuestions.map((_question) => DEFAULT_SCORING_RATING)
            );
        }
    }, [currentComment, ratingQuestions]);

    return (
        <Stack
            spacing={3}
            sx={{
                backgroundColor: theme?.custom?.white,
                borderRadius: "4px",
                border: `1px solid ${theme?.custom?.borderGrey}`,
                padding: "16px",
            }}
        >
            <ScoringFeedbackHeader
                user={user}
                date={moment(currentComment?.modified)}
                periodIndex={currentComment?.iterator}
            />
            <Stack
                direction="row"
                sx={{
                    flexWrap: "wrap",
                    gap: "24px",
                }}
            >
                <ScoringCommentPanel
                    value={commentText}
                    onChange={onCommentTextChange}
                />
                {ratingQuestions?.length > 0 && (
                    <Box sx={{ flexGrow: 1, minWidth: "300px" }}>
                        <ScoringRatingPanel
                            ratings={currentRatings}
                            ratingCriteria={ratingQuestions}
                            headerText="Provide your ratings"
                            onRatingChange={onRatingChange}
                        />
                    </Box>
                )}
            </Stack>
            <Stack
                direction="row"
                spacing={2}
            >
                <IwButton
                    variant="contained"
                    disabled={isSubmitButtonDisabled}
                    loading={savingComment}
                    onClick={saveComment}
                >
                    {!!currentComment ? "Save" : "Post"}
                </IwButton>
            </Stack>
        </Stack>
    );
};

export default memo(ScoringFeedbackSection);
